<template>
  <div>
    <div class="main-content fe-tracking-page">
      <div class="container py-4 py-lg-5">
        <header class="fe-tracking-page__header">
          <div class="fe-tracking-page__icon-wrap fe-service-icon">
            <i class="fas fa-screwdriver-wrench"></i>
          </div>
          <h1 class="fe-tracking-page__title">Service Tracking</h1>
          <p class="fe-tracking-page__subtitle">
            Enter the service tracking number from your bill to see repair status in real time.
          </p>
        </header>

        <div class="row g-4 align-items-start">
          <div v-if="isLoggedIn" class="col-lg-3 col-md-4">
            <account-sidebar />
          </div>
          <div :class="isLoggedIn ? 'col-lg-9 col-md-8' : 'col-12'">

            <div class="fe-tracking-search-card">
              <form @submit.prevent="handleTrack" class="fe-tracking-search">
                <div class="fe-tracking-search__input-wrap">
                  <i class="fas fa-search fe-tracking-search__icon"></i>
                  <input
                    v-model="searchRef"
                    type="text"
                    class="fe-tracking-search__input"
                    placeholder="Enter service tracking no. (e.g. LH/SER/0002)"
                    :disabled="loading"
                  />
                </div>
                <button type="submit" class="fe-tracking-search__btn" :disabled="loading || !searchRef.trim()">
                  <span v-if="loading" class="fe-tracking-search__spinner"></span>
                  <span v-else>Track Service</span>
                </button>
              </form>
            </div>

            <div v-if="isLoggedIn && !trackingData && !error && !loading" class="fe-tracking-user-orders">
              <div class="fe-tracking-user-orders__head">
                <h2 class="fe-tracking-user-orders__title">
                  <i class="fas fa-screwdriver-wrench"></i> Your Services
                </h2>
                <span v-if="userServices.length" class="fe-tracking-user-orders__count">
                  {{ userServices.length }} service(s)
                </span>
              </div>

              <div v-if="userServicesLoading" class="fe-tracking-state">
                <div class="fe-tracking-spinner"></div>
                <p>Loading your services...</p>
              </div>

              <div v-else-if="userServices.length === 0" class="fe-tracking-empty">
                <i class="fas fa-inbox"></i>
                <p>No services found for your account.</p>
              </div>

              <div v-else class="fe-tracking-orders-grid">
                <div
                  v-for="s in userServices"
                  :key="s.service.id"
                  class="fe-tracking-order-card"
                  @click="selectService(s)"
                >
                  <div class="fe-tracking-order-card__top">
                    <div>
                      <span class="fe-tracking-order-card__ref">{{ s.service.reference }}</span>
                      <span class="fe-tracking-order-card__date">{{ formatDate(s.service.date) }}</span>
                    </div>
                    <span class="fe-tracking-order-card__status is-progress">
                      {{ s.service.current_status }}
                    </span>
                  </div>
                  <div class="fe-tracking-order-card__bottom">
                    <span class="fe-tracking-order-card__total">{{ s.service.item_name || 'Service item' }}</span>
                    <span class="fe-tracking-order-card__arrow"><i class="fas fa-chevron-right"></i></span>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="error && !loading" class="fe-tracking-error" role="alert">
              <div class="fe-tracking-error__icon"><i class="fas fa-exclamation-triangle"></i></div>
              <div>
                <h3 class="fe-tracking-error__title">Service Not Found</h3>
                <p class="fe-tracking-error__text">{{ error }}</p>
              </div>
            </div>

            <div v-if="trackingData && !loading" class="fe-tracking-result">
              <button v-if="isLoggedIn" type="button" class="fe-tracking-back-btn" @click="clearTracking">
                <i class="fas fa-arrow-left"></i> Back to all services
              </button>

              <div class="fe-tracking-result__header">
                <div class="fe-tracking-result__info">
                  <div class="fe-tracking-result__ref-row">
                    <span class="fe-tracking-result__label">Service Tracking No</span>
                    <h2 class="fe-tracking-result__ref">{{ trackingData.service.reference }}</h2>
                  </div>
                  <div class="fe-tracking-result__meta">
                    <div class="fe-tracking-result__meta-item">
                      <i class="far fa-calendar-alt"></i>
                      <span>{{ formatDate(trackingData.service.date) }}</span>
                    </div>
                    <div v-if="trackingData.service.item_name" class="fe-tracking-result__meta-item">
                      <i class="fas fa-mobile-alt"></i>
                      <span>{{ trackingData.service.item_name }}</span>
                    </div>
                    <div v-if="trackingData.service.serial_number" class="fe-tracking-result__meta-item">
                      <i class="fas fa-barcode"></i>
                      <span>{{ trackingData.service.serial_number }}</span>
                    </div>
                  </div>
                </div>
                <div class="fe-tracking-result__status-badge is-progress">
                  <i class="fas fa-info-circle"></i>
                  {{ trackingData.service.current_status }}
                </div>
              </div>

              <div class="fe-tracking-timeline-card">
                <h3 class="fe-tracking-timeline-card__title"><i class="fas fa-route"></i> Service Timeline</h3>
                <div class="fe-tracking-timeline">
                  <div
                    v-for="(step, idx) in trackingData.timeline"
                    :key="step.key"
                    class="fe-tracking-timeline__step"
                    :class="{
                      'is-completed': step.status === 'completed',
                      'is-active': step.status === 'in_progress',
                      'is-pending': step.status === 'pending',
                    }"
                  >
                    <div class="fe-tracking-timeline__connector">
                      <div v-if="idx > 0" class="fe-tracking-timeline__line" :class="{ 'is-filled': step.status === 'completed' }"></div>
                      <div class="fe-tracking-timeline__dot">
                        <i v-if="step.status === 'completed'" class="fas fa-check"></i>
                        <i v-else-if="step.status === 'in_progress'" class="fas fa-spinner fa-pulse"></i>
                        <span v-else class="fe-tracking-timeline__dot-num">{{ step.step }}</span>
                      </div>
                    </div>
                    <div class="fe-tracking-timeline__content">
                      <div class="fe-tracking-timeline__icon-bg"><i :class="'fas ' + step.icon"></i></div>
                      <div class="fe-tracking-timeline__text">
                        <h4 class="fe-tracking-timeline__title">{{ step.title }}</h4>
                        <p class="fe-tracking-timeline__desc">{{ step.description }}</p>
                        <span v-if="step.date" class="fe-tracking-timeline__date">
                          <i class="far fa-clock"></i> {{ formatDateTime(step.date) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="fe-tracking-details-row">
                <div class="fe-tracking-info-card">
                  <h3 class="fe-tracking-info-card__title"><i class="fas fa-user"></i> Customer Details</h3>
                  <div class="fe-tracking-info-card__grid">
                    <div class="fe-tracking-info-card__item">
                      <span class="fe-tracking-info-card__label">Name</span>
                      <span class="fe-tracking-info-card__value">{{ trackingData.service.customer_name || '—' }}</span>
                    </div>
                    <div class="fe-tracking-info-card__item">
                      <span class="fe-tracking-info-card__label">Phone</span>
                      <span class="fe-tracking-info-card__value">{{ trackingData.service.customer_phone || '—' }}</span>
                    </div>
                  </div>
                </div>

                <div class="fe-tracking-info-card">
                  <h3 class="fe-tracking-info-card__title"><i class="fas fa-wrench"></i> Service Details</h3>
                  <div class="fe-tracking-info-card__grid">
                    <div class="fe-tracking-info-card__item">
                      <span class="fe-tracking-info-card__label">Problem / Nature of Work</span>
                      <span class="fe-tracking-info-card__value">{{ trackingData.service.nature_of_work || '—' }}</span>
                    </div>
                    <div v-if="trackingData.service.return_date" class="fe-tracking-info-card__item">
                      <span class="fe-tracking-info-card__label">Expected Return</span>
                      <span class="fe-tracking-info-card__value">{{ formatDate(trackingData.service.return_date) }}</span>
                    </div>
                    <div v-if="trackingData.service.estimate_amount" class="fe-tracking-info-card__item">
                      <span class="fe-tracking-info-card__label">Estimate</span>
                      <span class="fe-tracking-info-card__value">LKR {{ formatMoney(trackingData.service.estimate_amount) }}</span>
                    </div>
                    <div v-if="trackingData.service.deposit" class="fe-tracking-info-card__item">
                      <span class="fe-tracking-info-card__label">Deposit</span>
                      <span class="fe-tracking-info-card__value">LKR {{ formatMoney(trackingData.service.deposit) }}</span>
                    </div>
                  </div>
                  <p v-if="trackingData.service.problem_note" class="fe-service-note">{{ trackingData.service.problem_note }}</p>
                </div>

                <div v-if="trackingData.items && trackingData.items.length" class="fe-tracking-info-card">
                  <h3 class="fe-tracking-info-card__title"><i class="fas fa-list"></i> Service Items</h3>
                  <ul class="fe-service-items">
                    <li v-for="(item, i) in trackingData.items" :key="i">
                      {{ item.product_name }} <span v-if="item.qty">× {{ item.qty }}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div v-if="!isLoggedIn && !trackingData && !error && !loading" class="fe-tracking-help">
              <div class="fe-tracking-help__card">
                <div class="fe-tracking-help__icon"><i class="fas fa-info-circle"></i></div>
                <h3>How to find your service tracking number?</h3>
                <ul>
                  <li>Check the service receipt / bill from our shop</li>
                  <li>The number looks like <strong>LH/SER/0002</strong></li>
                  <li><router-link to="/login">Sign in</router-link> to view services linked to your phone number</li>
                </ul>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
    <cta-area />
  </div>
</template>

<script>
import CtaArea from '@/components/cta/cta-area.vue'
import AccountSidebar from '@/components/account/AccountSidebar.vue'

export default {
  name: 'ServiceTracking',
  components: { CtaArea, AccountSidebar },
  data() {
    return {
      searchRef: '',
      loading: false,
      error: null,
      trackingData: null,
      userServices: [],
      userServicesLoading: false,
      user: null,
    }
  },
  computed: {
    isLoggedIn() {
      return !!this.user
    },
  },
  mounted() {
    try {
      this.user = JSON.parse(localStorage.getItem('user')) || null
    } catch {
      this.user = null
    }

    if (this.$route.query.ref) {
      this.searchRef = this.$route.query.ref
      this.handleTrack()
    } else if (this.isLoggedIn) {
      this.fetchUserServices()
    }
  },
  methods: {
    async handleTrack() {
      const ref = this.searchRef.trim()
      if (!ref) return

      this.loading = true
      this.error = null
      this.trackingData = null

      try {
        const { data } = await this.$axios.get('/track-service', { params: { reference: ref } })
        this.trackingData = data
      } catch (err) {
        this.error = err.response?.data?.error || 'Unable to track service. Please try again.'
      } finally {
        this.loading = false
      }
    },

    async fetchUserServices() {
      if (!this.user?.id) return
      this.userServicesLoading = true
      try {
        const { data } = await this.$axios.get(`/track-service/user/${this.user.id}`)
        this.userServices = Array.isArray(data) ? data : []
      } catch {
        this.userServices = []
      } finally {
        this.userServicesLoading = false
      }
    },

    selectService(s) {
      this.trackingData = s
      this.error = null
      this.searchRef = s.service.reference || ''
    },

    clearTracking() {
      this.trackingData = null
      this.error = null
      this.searchRef = ''
    },

    formatDate(dateStr) {
      if (!dateStr) return '—'
      const d = new Date(dateStr)
      if (Number.isNaN(d.getTime())) return String(dateStr)
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    },

    formatDateTime(dateStr) {
      if (!dateStr) return '—'
      const d = new Date(dateStr)
      if (Number.isNaN(d.getTime())) return String(dateStr)
      return d.toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    },

    formatMoney(val) {
      const n = Number(val)
      if (Number.isNaN(n)) return String(val ?? '—')
      return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
  },
}
</script>

<style scoped>
@import './order-tracking.shared.css';

.fe-service-icon {
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%) !important;
  box-shadow: 0 8px 28px rgba(14, 165, 233, 0.3) !important;
}

.fe-service-note {
  margin: 12px 0 0;
  padding: 12px;
  background: #f8fafc;
  border-radius: 10px;
  color: #475569;
  font-size: 14px;
}

.fe-service-items {
  margin: 0;
  padding-left: 1.1rem;
  color: #334155;
}

.fe-service-items li {
  margin-bottom: 6px;
}
</style>
